<template>
    <div v-if="debugBarEnabled" class="debugbar" :class="{ open }">
        <div class="debugbar-strip" @click="open = !open">
            <label class="debugbar-toggle" @click.stop>
                <input type="checkbox" @change="onHideChecked" />
                <span>Hide DebugBar</span>
            </label>
            <span class="debugbar-title">🐛 Debug</span>
            <span class="debugbar-stat" :class="severityClass(pageLoadMs)">
                page load {{ formatMs(pageLoadMs) }}{{ pageLoadComplete ? '' : '…' }}
            </span>
            <span class="debugbar-stat">{{ requests.length }} req</span>
            <span class="debugbar-stat" :class="severityClass(slowest?.durationMs)">
                slowest {{ slowest ? `${slowest.durationMs}ms` : '—' }}
            </span>
            <span class="debugbar-stat" v-if="pendingCount">{{ pendingCount }} pending</span>
            <span class="debugbar-spacer"></span>
            <button class="debugbar-btn" @click.stop="clearDebugEntries()">Clear</button>
            <button class="debugbar-btn" @click.stop="close()">✕</button>
        </div>

        <div v-if="open" class="debugbar-panel" :style="{ height: panelHeight + 'px' }">
            <div
                class="debugbar-resize-handle"
                :class="{ resizing }"
                @mousedown="startResize"
            ><span class="debugbar-resize-grip"></span></div>
            <div class="debugbar-panel-body">
                <div class="debugbar-row debugbar-header">
                    <span class="col-type">Type</span>
                    <span class="col-label">Request</span>
                    <span class="col-source">Source</span>
                    <span class="col-status">Status</span>
                    <span class="col-duration">Time</span>
                    <span class="col-server">Server</span>
                    <span class="col-since">Since Start</span>
                    <span class="col-size">Size</span>
                </div>
                <div class="debugbar-rows">
                    <div v-for="e in reversedEntries" :key="e.id" class="debugbar-row" :class="'type-' + e.type">
                        <span class="col-type">{{ typeLabel(e.type) }}</span>
                        <span class="col-label" :title="e.url || e.label">{{ e.method ? e.method + ' ' : '' }}{{ e.url || e.label }}</span>
                        <span class="col-source" :class="sourceClass(e)">{{ sourceLabel(e) }}</span>
                        <span class="col-status" :class="statusClass(e)">{{ e.status ?? '' }}</span>
                        <span class="col-duration" :class="severityClass(e.durationMs)">{{ e.durationMs != null ? e.durationMs + 'ms' : '…' }}</span>
                        <span class="col-server" :title="e.serverMs != null && e.durationMs != null ? `${e.durationMs - e.serverMs}ms network/queue overhead` : ''">{{ e.serverMs != null ? e.serverMs + 'ms' : '' }}</span>
                        <span class="col-since">{{ formatSince(e) }}</span>
                        <span class="col-size">{{ e.sizeKB != null ? e.sizeKB + 'KB' : '' }}</span>
                    </div>
                    <div v-if="reversedEntries.length === 0" class="debugbar-empty">No requests captured yet.</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { debugBarEnabled, debugEntries, clearDebugEntries, setDebugBarEnabled, pageStartTime } from '@/utility/debugStore'

const open = ref(false)

const PANEL_HEIGHT_KEY = 'debugBarPanelHeight'
const MIN_PANEL_HEIGHT = 120
const panelHeight = ref(Number(localStorage.getItem(PANEL_HEIGHT_KEY)) || Math.round(window.innerHeight * 0.4))

const resizing = ref(false)
let resizeStartY = 0
let resizeStartHeight = 0

function startResize (e) {
    resizing.value = true
    resizeStartY = e.clientY
    resizeStartHeight = panelHeight.value
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onResize)
    document.addEventListener('mouseup', stopResize)
    e.preventDefault()
}

function onResize (e) {
    if (!resizing.value) return
    // Dragging the handle up should grow the panel (it's anchored to the
    // bottom of the screen), so height grows as clientY decreases.
    const delta = resizeStartY - e.clientY
    const maxHeight = window.innerHeight - 80
    panelHeight.value = Math.min(Math.max(resizeStartHeight + delta, MIN_PANEL_HEIGHT), maxHeight)
}

function stopResize () {
    if (!resizing.value) return
    resizing.value = false
    document.body.style.userSelect = ''
    document.removeEventListener('mousemove', onResize)
    document.removeEventListener('mouseup', stopResize)
    localStorage.setItem(PANEL_HEIGHT_KEY, String(panelHeight.value))
}

onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onResize)
    document.removeEventListener('mouseup', stopResize)
})

// Ticks so "page load" (in the collapsed strip) and "Since Start" (in the
// panel, for still-pending rows) run live instead of only updating when a
// new entry arrives. Runs from mount until the page's load event fires;
// after that it only needs to run while the panel is open.
const nowTick = ref(Date.now())
let tickTimer = null

function ensureTicking () {
    if (tickTimer) return
    tickTimer = setInterval(() => { nowTick.value = Date.now() }, 100)
}

function stopTicking () {
    if (tickTimer) {
        clearInterval(tickTimer)
        tickTimer = null
    }
}

ensureTicking()

const pageLoadEntry = computed(() => debugEntries.find(e => e.type === 'pageload'))
const pageLoadComplete = computed(() => {
    const ms = pageLoadEntry.value?.durationMs
    return typeof ms === 'number' && ms > 0
})
const pageLoadMs = computed(() => {
    if (pageLoadComplete.value) return pageLoadEntry.value.durationMs
    return Math.max(0, Math.round(nowTick.value - pageStartTime))
})

watch(open, (isOpen) => {
    if (isOpen || !pageLoadComplete.value) ensureTicking()
    else stopTicking()
})

watch(pageLoadEntry, (entry) => {
    const isComplete = typeof entry?.durationMs === 'number' && entry.durationMs > 0
    if (isComplete && !open.value) stopTicking()
})

onBeforeUnmount(() => stopTicking())

const reversedEntries = computed(() => [...debugEntries].reverse())
const requests = computed(() => debugEntries.filter(e => e.type === 'api'))
const pendingCount = computed(() => requests.value.filter(e => e.status === 'pending').length)
const slowest = computed(() => {
    const done = debugEntries.filter(e => e.durationMs != null)
    if (!done.length) return null
    return done.reduce((a, b) => (b.durationMs > a.durationMs ? b : a))
})

function typeLabel (type) {
    return { api: 'API', longtask: 'BLOCK', paint: 'PAINT', pageload: 'LOAD' }[type] || type
}

// Where the data for this row came from: a real network request, a Pinia
// cache hit (no request fired), or unknown/not applicable (non-api rows).
function sourceLabel (e) {
    if (e.type !== 'api') return ''
    if (e.source === 'pinia') return 'pinia'
    if (e.source === 'network') return 'network'
    return 'other'
}

function sourceClass (e) {
    if (e.type !== 'api') return ''
    if (e.source === 'pinia') return 'source-pinia'
    if (e.source === 'network') return 'source-network'
    return 'source-other'
}

function statusClass (e) {
    if (e.type !== 'api') return ''
    if (e.status === 'pending') return 'status-pending'
    if (typeof e.status === 'number' && e.status >= 400) return 'status-error'
    if (e.status === 'ERR' || (typeof e.status === 'string' && e.status.startsWith('ERR'))) return 'status-error'
    return 'status-ok'
}

function severityClass (ms) {
    if (ms == null) return ''
    if (ms > 1500) return 'sev-bad'
    if (ms > 500) return 'sev-warn'
    return 'sev-good'
}

function formatMs (ms) {
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
}

function formatSince (e) {
    const isPending = e.type === 'api' && e.status === 'pending'
    const point = isPending ? nowTick.value : e.at
    const ms = Math.max(0, Math.round(point - pageStartTime))
    return formatMs(ms)
}

function close () {
    open.value = false
    setDebugBarEnabled(false)
}

function onHideChecked (event) {
    const checked = Boolean(event?.target?.checked)
    if (!checked) return
    open.value = false
    setDebugBarEnabled(false)
}
</script>
